import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

import { IAPIResponse, StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import { TProductVendor } from "redux/reducers/models/product";
import { useAppDispatch } from "hooks/redux";
import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import { ButtonNew as Button } from "components/buttonNew";

import { vendorContacts, vendorLocation } from "./vendorFormState";
import "./vendors.scss";

type VendorSortKey = "title" | "rep" | "location";

interface IVendorListProps {
  listPath: string;
  newPath: string;
  editPath: (id: number) => string;
  load: (token?: CancelToken) => Promise<IAPIResponse<TProductVendor[]>>;
}

export default function VendorList(props: IVendorListProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [vendors, setVendors] = useState<TProductVendor[] | undefined>(undefined);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<VendorSortKey>("title");
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadVendors(source.token);
    return () => source.cancel();
  }, []);

  async function loadVendors(token?: CancelToken) {
    const res = await props.load(token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error retrieving vendor list")));
      setVendors([]);
      return;
    }
    setVendors(res.data ?? []);
  }

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (vendors ?? [])
      .filter(vendor => {
        if (!q) {
          return true;
        }
        const contacts = vendorContacts(vendor);
        const haystack = [vendor.title, vendorLocation(vendor), ...contacts.flatMap(contact => [contact.name, contact.email])]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => {
        const left = sortValue(a, sortKey);
        const right = sortValue(b, sortKey);
        if (!left && right) {
          return 1;
        }
        if (left && !right) {
          return -1;
        }
        return left.localeCompare(right) * sortDir;
      });
  }, [vendors, query, sortKey, sortDir]);

  function handleSort(key: VendorSortKey, direction: "asc" | "desc") {
    setSortKey(key);
    setSortDir(direction === "asc" ? 1 : -1);
  }

  function openVendor(id: number) {
    history.push(props.editPath(id));
  }

  const searching = query.trim() !== "";

  return (
    <Page
      className="vendor-screen"
      title={t("Vendors")}
      narrow
      breadcrumbs={[
        { label: t("Settings") },
        { label: t("Vendors"), url: props.listPath },
      ]}
      primaryAction={{ content: t("New Vendor"), action: () => history.push(props.newPath) }}
    >
      <div className="vendor-toolbar">
        <label className="vendor-search">
          <span className="vendor-sr">{t("Search vendors")}</span>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input
            type="search"
            value={query}
            placeholder={t("Search vendors, reps or cities…")}
            autoComplete="off"
            onChange={event => setQuery(event.target.value)}
          />
        </label>
      </div>

      <DataTable<Record<VendorSortKey, string>>
        columns={[
          { label: t("Vendor"), id: "title" },
          { label: t("Sales rep"), id: "rep" },
          { label: t("Contact") },
          { label: t("Location"), id: "location" },
        ]}
        loading={vendors === undefined}
        sort={handleSort}
        textWrap
      >
        {vendors?.length === 0 ? (
          <tr>
            <td colSpan={4}>
              <div className="vendor-empty">
                <strong>{t("No vendors yet")}</strong>
                {t("Add a vendor to start tracking reps and contact details.")}
                <div>
                  <Button type="primary" onClick={() => history.push(props.newPath)}>
                    {t("New Vendor")}
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : visible.length === 0 ? (
          <tr>
            <td colSpan={4}>
              <div className="vendor-empty">
                <strong>{t("No vendors match")}</strong>
                {t("Try a different name, rep or city.")}
                <div>
                  <Button type="secondary" onClick={() => setQuery("")}>
                    {t("Clear search")}
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          visible.map(vendor => {
            const contacts = vendorContacts(vendor).filter(
              contact => contact.name.trim() || contact.role.trim() || contact.email.trim() || contact.phone.trim(),
            );
            const rep = contacts.find(contact => contact.name.trim()) ?? contacts[0];
            const extra = rep ? contacts.length - 1 : 0;
            const location = vendorLocation(vendor);
            return (
              <tr key={vendor.id} className="clickable" onClick={() => openVendor(vendor.id)}>
                <td>
                  <div className="vendor-main">{vendor.title}</div>
                  <div className="vendor-meta vendor-sub">
                    {rep?.name?.trim() ? rep.name : "–"}
                    {location ? (
                      <>
                        <br />
                        {location}
                      </>
                    ) : null}
                  </div>
                </td>
                <td>
                  {rep?.name?.trim() ? (
                    <>
                      <div className="vendor-main">
                        {rep.name}
                        {extra > 0 ? <span className="vendor-more">+{extra}</span> : null}
                      </div>
                      {rep.role ? <div className="vendor-sub">{rep.role}</div> : null}
                    </>
                  ) : (
                    <span className="vendor-none">–</span>
                  )}
                </td>
                <td>
                  {rep?.email || rep?.phone ? (
                    <>
                      {rep.email ? (
                        <a className="vendor-link" href={`mailto:${rep.email}`} onClick={event => event.stopPropagation()}>
                          {rep.email}
                        </a>
                      ) : null}
                      {rep.email && rep.phone ? ", " : null}
                      {rep.phone ? (
                        <a
                          className="vendor-link"
                          href={`tel:${rep.phone.replace(/[^\d+]/g, "")}`}
                          onClick={event => event.stopPropagation()}
                        >
                          {rep.phone}
                        </a>
                      ) : null}
                    </>
                  ) : (
                    <span className="vendor-none">–</span>
                  )}
                </td>
                <td>{location || <span className="vendor-none">–</span>}</td>
              </tr>
            );
          })
        )}
      </DataTable>
      {vendors && vendors.length > 0 && visible.length > 0 ? (
        <p className="vendor-foot">
          {searching
            ? t("Showing {{shown}} of {{total}} vendors", { shown: visible.length, total: vendors.length })
            : t("{{count}} vendors", { count: vendors.length })}
        </p>
      ) : null}
    </Page>
  );
}

function sortValue(vendor: TProductVendor, key: VendorSortKey): string {
  if (key === "title") {
    return vendor.title ?? "";
  }
  if (key === "rep") {
    return vendorContacts(vendor).find(contact => contact.name.trim())?.name ?? "";
  }
  return vendorLocation(vendor);
}
